import type { EmailDraft } from '../useData';
import type { ComposeBodyDocument } from './types';
import { classifyBodyKind, htmlHasSlateRichFormatting } from './classifyBodyKind';
import { composeImportedMessageToEditableEditorHtml } from '../utils/unlockReplyForwardEditing';
import { isPlaceholderEditorHtml } from './editorHtml';
import { mergeQuotedBlocks } from './quotedBlockNormalize';
import {
  normalizeLoadedOutboundComposeDoc,
  parseSavedTemplateOutboundHtml,
} from './composeDocFromTemplate';

function isComposeBodyDocument(value: unknown): value is ComposeBodyDocument {
  if (!value || typeof value !== 'object') return false;
  const v = value as ComposeBodyDocument;
  return (v.bodyKind === 'plain' || v.bodyKind === 'rich') && typeof v.userBody === 'string';
}

function bodyHtmlFromDraft(draft: EmailDraft): string {
  return draft.bodyHtml?.trim() || '';
}

function composeDocFromDraftParts(draft: EmailDraft): ComposeBodyDocument {
  const htmlStr = bodyHtmlFromDraft(draft);
  const outboundParsed = htmlStr ? parseSavedTemplateOutboundHtml(htmlStr) : null;
  if (outboundParsed) {
    return normalizeLoadedOutboundComposeDoc(outboundParsed, htmlStr);
  }

  const bodyKind =
    draft.bodyKind === 'plain' || draft.bodyKind === 'rich'
      ? draft.bodyKind
      : classifyBodyKind(draft.bodyHtml, draft.bodyText);

  if (bodyKind === 'plain') {
    const userBody = composeImportedMessageToEditableEditorHtml(
      draft.bodyHtml,
      draft.bodyText,
      { bodyKind: 'plain' },
    );
    return {
      bodyKind: 'rich',
      userBody: userBody.trim() || '<p><br></p>',
    };
  }

  return {
    bodyKind: 'rich',
    userBody: htmlStr || '<p><br></p>',
  };
}

export function composeDocFromDraft(draft: EmailDraft): ComposeBodyDocument {
  const draftHtml = bodyHtmlFromDraft(draft);
  const outboundParsed = draftHtml ? parseSavedTemplateOutboundHtml(draftHtml) : null;

  if (draft.composeState && isComposeBodyDocument(draft.composeState)) {
    let state = draft.composeState;
    if (!htmlHasSlateRichFormatting(state.userBody) && draftHtml) {
      if (outboundParsed && htmlHasSlateRichFormatting(outboundParsed.userBody)) {
        state = {
          ...state,
          userBody: outboundParsed.userBody,
          signature: state.signature ?? outboundParsed.signature,
          quoted:
            state.quoted && outboundParsed.quoted
              ? mergeQuotedBlocks(state.quoted, outboundParsed.quoted)
              : state.quoted ?? outboundParsed.quoted,
        };
      }
    } else if (state.quoted && outboundParsed?.quoted) {
      state = {
        ...state,
        quoted: mergeQuotedBlocks(state.quoted, outboundParsed.quoted),
      };
    }

    if (!isPlaceholderEditorHtml(state.userBody) || state.quoted || state.signature) {
      const loaded = normalizeLoadedOutboundComposeDoc(
        {
          ...state,
          bodyKind: 'rich',
        },
        draftHtml,
      );
      if (
        !isPlaceholderEditorHtml(loaded.userBody) ||
        loaded.quoted ||
        loaded.signature ||
        !outboundParsed
      ) {
        return loaded;
      }
    }
  }

  return composeDocFromDraftParts(draft);
}
